import React, { useState, useEffect } from 'react';
import api from '../api';
import { RotateCcw, Search, Plus, Trash2 } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '';

const SHR_KEY = 'webb_shipment_return_draft';

const ShipmentReturns = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);

  const saved = loadDraft(SHR_KEY);
  const [shipmentNumber, setShipmentNumber] = useState(saved?.shipmentNumber || '');
  const [shipmentData, setShipmentData] = useState(saved?.shipmentData || null);
  const [lookupError, setLookupError] = useState('');
  const [returnDate, setReturnDate] = useState(saved?.returnDate || new Date().toISOString().split('T')[0]);
  const [returnItems, setReturnItems] = useState(saved?.returnItems || []);
  const [remarks, setRemarks] = useState(saved?.remarks || '');
  const [saving, setSaving] = useState(false);

  useAutosave(SHR_KEY, { shipmentNumber, shipmentData, returnDate, returnItems, remarks });

  const fetchReturns = async () => {
    try {
      const data = await api.request('/api/shipment-returns');
      setReturns(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReturns(); }, []);

  const lookupShipment = async () => {
    if (!shipmentNumber) return;
    setLookupError('');
    setShipmentData(null);
    setReturnItems([]);
    try {
      const items = await api.request(`/api/shipments-received/${shipmentNumber}`);
      if (!items || items.length === 0) {
        setLookupError(`Shipment #${shipmentNumber} not found`);
        return;
      }
      setShipmentData(items);
      setReturnItems(items.map(s => ({
        variant_id: s.variant_id || '',
        product_name: s.product_name || '',
        sku: s.sku || '',
        cost_price: s.cost_price || 0,
        max_qty: s.quantity || 0,
        quantity: 0,
        checked: false,
      })));
    } catch (err) {
      setLookupError(err.message || 'Shipment not found');
    }
  };

  const toggleReturnItem = (idx) => {
    setReturnItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, checked: !item.checked, quantity: !item.checked ? item.max_qty : 0 } : item
    ));
  };

  const updateReturnQty = (idx, qty) => {
    const parsed = Math.min(Math.max(0, parseInt(qty) || 0), returnItems[idx].max_qty);
    setReturnItems(prev => prev.map((item, i) =>
      i === idx ? { ...item, quantity: parsed, checked: parsed > 0 } : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const items = returnItems.filter(i => i.quantity > 0);
    if (!shipmentNumber) { alert('Enter a Shipment #'); return; }
    if (items.length === 0) { alert('Set quantity > 0 for at least one item to return'); return; }

    setSaving(true);
    try {
      await api.request('/api/shipment-returns', {
        method: 'POST',
        body: JSON.stringify({
          shipment_number: parseInt(shipmentNumber),
          return_date: returnDate,
          remarks,
          line_items: items.map(i => ({
            variant_id: i.variant_id,
            product_name: i.product_name,
            sku: i.sku,
            quantity: i.quantity,
            cost_price: i.cost_price,
          })),
        }),
      });
      clearDraft(SHR_KEY);
      setShowForm(false);
      setShipmentNumber(''); setShipmentData(null); setReturnItems([]); setRemarks('');
      setReturnDate(new Date().toISOString().split('T')[0]);
      fetchReturns();
    } catch (err) {
      alert('Failed: ' + err.message);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this return? Inventory will be restored.')) return;
    try {
      await api.request(`/api/shipment-returns/${id}`, { method: 'DELETE' });
      fetchReturns();
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const filtered = returns.filter(r =>
    !search || r.return_number?.toLowerCase().includes(search.toLowerCase()) ||
    r.vendor_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="shipment-returns-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Shipments Returned</h1>
          <p className="text-sm text-muted-foreground mt-1">Return items from a received shipment — deducts from inventory and products</p>
        </div>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
          data-testid="new-shipment-return-btn">
          <Plus className="h-4 w-4" /> New Return
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-card border rounded-lg p-6 space-y-4" data-testid="shipment-return-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Shipment # *</label>
              <div className="flex gap-2">
                <input type="number" min="1" value={shipmentNumber} onChange={(e) => setShipmentNumber(e.target.value)}
                  placeholder="1" className="flex-1 px-3 py-2 border border-input rounded-md text-sm"
                  data-testid="return-shipment-number" />
                <button type="button" onClick={lookupShipment}
                  className="px-3 py-2 bg-secondary text-secondary-foreground rounded-md text-sm hover:bg-secondary/80"
                  data-testid="lookup-shipment-btn">
                  <Search className="h-4 w-4" />
                </button>
              </div>
              {lookupError && <p className="text-red-500 text-xs mt-1">{lookupError}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Return Date</label>
              <input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md text-sm" data-testid="shipment-return-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Remarks</label>
              <input type="text" value={remarks} onChange={(e) => setRemarks(e.target.value)}
                placeholder="Reason for return" className="w-full px-3 py-2 border border-input rounded-md text-sm" />
            </div>
          </div>

          {shipmentData && (
            <div className="mt-2">
              <p className="text-sm text-muted-foreground mb-2">
                Vendor: <span className="font-medium text-foreground">{shipmentData[0]?.vendor_name || 'N/A'}</span>
              </p>
              <table className="w-full text-sm border">
                <thead>
                  <tr className="bg-muted/30 border-b">
                    <th className="text-center px-3 py-2 w-10">Return</th>
                    <th className="text-left px-3 py-2">Product</th>
                    <th className="text-left px-3 py-2">SKU</th>
                    <th className="text-right px-3 py-2">Cost</th>
                    <th className="text-center px-3 py-2">Received</th>
                    <th className="text-center px-3 py-2">Return Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {returnItems.map((item, idx) => (
                    <tr key={idx} className={`border-b ${item.checked ? 'bg-red-50/50' : ''}`}>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={!!item.checked}
                          onChange={() => toggleReturnItem(idx)}
                          className="h-4 w-4 rounded border-input accent-red-600"
                          data-testid={`shipment-return-check-${idx}`} />
                      </td>
                      <td className="px-3 py-2">{item.product_name}</td>
                      <td className="px-3 py-2 text-muted-foreground">{item.sku}</td>
                      <td className="px-3 py-2 text-right">{fmtCurrency(item.cost_price)}</td>
                      <td className="px-3 py-2 text-center">{item.max_qty}</td>
                      <td className="px-3 py-2 text-center">
                        <input type="number" min="0" max={item.max_qty}
                          value={item.quantity} onChange={(e) => updateReturnQty(idx, e.target.value)}
                          className="w-20 px-2 py-1 border border-input rounded text-center text-sm disabled:opacity-40"
                          disabled={!item.checked}
                          data-testid={`shipment-return-qty-${idx}`} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button type="submit" disabled={saving || !shipmentData}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium disabled:opacity-50"
              data-testid="submit-shipment-return-btn">
              {saving ? 'Processing...' : 'Process Return'}
            </button>
            <button type="button" onClick={() => { setShowForm(false); clearDraft(SHR_KEY); }}
              className="px-4 py-2 border rounded-md hover:bg-muted text-sm">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input type="text" placeholder="Search returns..." value={search} onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-input rounded-lg text-sm" />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <RotateCcw className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No shipment returns found</p>
        </div>
      ) : (
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-4 py-3 font-medium">Return #</th>
                <th className="text-left px-4 py-3 font-medium">Shipment #</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Vendor</th>
                <th className="text-right px-4 py-3 font-medium">Value</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className="border-b hover:bg-muted/20">
                  <td className="px-4 py-3 font-medium">{r.return_number}</td>
                  <td className="px-4 py-3">#{r.shipment_number}</td>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(r.return_date)}</td>
                  <td className="px-4 py-3">{r.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-medium text-red-600">-{fmtCurrency(r.total_value)}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(r.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-md">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ShipmentReturns;
